import React, { useEffect } from 'react';
import { HashRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import LandingPage from './Components/landingpage';
import MedicationAddition from './Components/Medication-Addition';
import CheckoutBilling from './Components/Checkout-billing';
import Inventory from './Components/Inventory';

import PharmaHomepage from './Components/pharma-homepage';
import PurchaseHistory from './Components/purchase-history';
import SalesHistory from './Components/sales-history';
import DistributorsHistory from './Components/distributors-history';
import PurchasePage from './Components/Purchase-page';
import BillingPage from './Components/billing-page';
import Notifications from './Components/Notifications';
import './App.css';

const IST_OFFSET_MINUTES = 330;
const SESSION_COOKIE_NAME = 'pharmaSessionActive';

const getNextISTMidnightInfo = () => {
  const now = new Date();
  const utcTimestamp = now.getTime() + now.getTimezoneOffset() * 60000;
  const istTimestamp = utcTimestamp + IST_OFFSET_MINUTES * 60000;
  const istNow = new Date(istTimestamp);
  const nextMidnightIST = new Date(istNow);
  nextMidnightIST.setHours(24, 0, 0, 0);
  const msUntilMidnightIST = nextMidnightIST.getTime() - istNow.getTime();
  const nextMidnightUTC = new Date(utcTimestamp + msUntilMidnightIST);
  return { msUntilMidnightIST, nextMidnightUTC };
};

const setSessionCookie = () => {
  if (typeof document === 'undefined') return;
  const { nextMidnightUTC } = getNextISTMidnightInfo();
  document.cookie = `${SESSION_COOKIE_NAME}=active; expires=${nextMidnightUTC.toUTCString()}; path=/; SameSite=Lax`;
};

const clearSessionCookie = () => {
  if (typeof document === 'undefined') return;
  document.cookie = `${SESSION_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
};

const hasSessionCookie = () => {
  if (typeof document === 'undefined') return false;
  return document.cookie
    .split(';')
    .map(cookie => cookie.trim())
    .some(cookie => cookie.startsWith(`${SESSION_COOKIE_NAME}=`));
};

// Component to handle keyboard shortcuts (must be inside Router)
function KeyboardShortcuts() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Ctrl/Cmd key combinations
      if (event.ctrlKey || event.metaKey) {
        const key = event.key.toLowerCase();
        
        // Ctrl+P → Navigate to Purchase Page
        if (key === 'p') {
          event.preventDefault(); // Prevent default browser print dialog
          if (location.pathname !== '/purchase-page') {
            navigate('/purchase-page');
          }
        }
        // Ctrl+I → Navigate to Inventory
        else if (key === 'i') {
          event.preventDefault(); // Prevent default browser behavior
          if (location.pathname !== '/inventory') {
            navigate('/inventory');
          }
        }
        // Ctrl+B → Navigate to Billing Page
        else if (key === 'b') {
          event.preventDefault(); // Prevent default browser behavior (bold formatting)
          if (location.pathname !== '/billing-page') {
            navigate('/billing-page');
          }
        }
        // Ctrl+H → Navigate to Pharma Homepage
        else if (key === 'h') {
          event.preventDefault(); // Prevent default browser behavior (history)
          if (location.pathname !== '/pharma-homepage') {
            navigate('/pharma-homepage');
          }
        }
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, location.pathname]);

  return null; // This component doesn't render anything
}

function MidnightLogoutGuard() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const clearUserSession = () => {
      const storageKeys = [
        'authToken',
        'userData',
        'userRole',
        'loginTime',
        'username',
        'userEmail',
        'userId'
      ];
      storageKeys.forEach((key) => {
        try {
          window.localStorage.removeItem(key);
        } catch (error) {
          console.warn('Unable to remove key from localStorage:', key, error);
        }
      });
    };

    const handleMidnightLogout = () => {
      clearUserSession();
      clearSessionCookie();
      if (location.pathname !== '/') {
        navigate('/');
      }
    };

    const ensureSessionCookie = () => {
      const hasAuthToken = !!window.localStorage.getItem('authToken');
      if (hasAuthToken && !hasSessionCookie()) {
        setSessionCookie();
      } else if (!hasAuthToken && hasSessionCookie()) {
        clearSessionCookie();
      }
    };

    ensureSessionCookie();

    const scheduleMidnightCheck = () => {
      const { msUntilMidnightIST } = getNextISTMidnightInfo();
      return window.setTimeout(() => {
        if (window.localStorage.getItem('authToken')) {
          handleMidnightLogout();
        }
      }, msUntilMidnightIST);
    };

    let midnightTimeoutId = scheduleMidnightCheck();

    const intervalId = window.setInterval(() => {
      ensureSessionCookie();
      // Only logout at midnight, don't logout just because cookie is missing
      // Cookie issues on hosted environments shouldn't force logout
    }, 60 * 1000);

    const visibilityHandler = () => {
      if (!document.hidden) {
        ensureSessionCookie();
      }
    };

    document.addEventListener('visibilitychange', visibilityHandler);

    return () => {
      window.clearTimeout(midnightTimeoutId);
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', visibilityHandler);
    };
  }, [navigate, location.pathname]);

  return null;
}

function AppContent() {
  return (
    <div className="App">
      <KeyboardShortcuts />
      <MidnightLogoutGuard />
      <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/medication-addition" element={<MedicationAddition />} />
          <Route path="/checkout-billing" element={<CheckoutBilling />} />
          <Route path="/inventory" element={<Inventory />} />
              
              <Route path="/pharma-homepage" element={<PharmaHomepage />} />
              <Route path="/purchase-history" element={<PurchaseHistory />} />
              <Route path="/sales-history" element={<SalesHistory />} />
              <Route path="/distributors-history" element={<DistributorsHistory />} />
              <Route path="/purchase-page" element={<PurchasePage />} />
              <Route path="/billing-page" element={<BillingPage />} />
              <Route path="/notifications" element={<Notifications />} />
            </Routes>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
